import React from "react";
import { Badge, Button } from "react-bootstrap";
import { ChevronDown, ChevronUp, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import { formatDateTime } from "../../trackTrace/mapUtils";
import { TrackTraceAsset, TrackTraceMapPayload } from "../../trackTrace/types";

interface Props {
    payload: TrackTraceMapPayload;
    selectedAsset?: TrackTraceAsset | null;
    collapsed: boolean;
    onSelectAsset: (asset: TrackTraceAsset) => void;
    onClear: () => void;
    onToggleCollapsed: (collapsed: boolean) => void;
}

const AssetBottomSheet: React.FC<Props> = ({ payload, selectedAsset, collapsed, onSelectAsset, onClear, onToggleCollapsed }) => {
    const { i18n } = useTranslation();
    const t = (key: string, options?: object) => (i18n.t as unknown as (key: string, options?: object) => string)(key, options);
    const primaryTag = selectedAsset?.tags.find((tag) => tag.is_primary) || selectedAsset?.tags[0];
    const topAssetTypes = [...payload.summary.asset_type_counts].sort((a, b) => b.count - a.count).slice(0, 2);
    const formatTranslatedDateTime = (value?: Date | string | null) => {
        const formatted = formatDateTime(value);

        return formatted === 'Unknown' ? t('track_trace.common.unknown') : formatted;
    };
    const freshnessLabel = (freshness?: string) => t(`track_trace.freshness.${freshness || 'unknown'}`, { defaultValue: freshness || t('track_trace.common.unknown') });

    if (collapsed && !selectedAsset) {
        return (
            <button className="asset-bottom-sheet asset-bottom-sheet-collapsed" onClick={() => onToggleCollapsed(false)}>
                <span>
                    <strong>{t('track_trace.assets.visible_assets', { count: payload.summary.visible_assets })}</strong>
                    <small>{topAssetTypes.map((item) => `${item.asset_type_name}: ${item.count}`).join(' - ') || t('track_trace.assets.total_assets', { count: payload.summary.total_assets })}</small>
                </span>
                <ChevronUp size={20} />
            </button>
        );
    }

    if (selectedAsset) {
        const freshness = selectedAsset.current_location?.freshness || 'unknown';
        return (
            <div className="asset-bottom-sheet">
                <div className="sheet-title-row">
                    <div>
                        <h2>{selectedAsset.name}</h2>
                        <p>{selectedAsset.code || t('track_trace.assets.no_asset_code')}</p>
                    </div>
                    <div className="sheet-actions">
                        <Button variant="link" onClick={() => onToggleCollapsed(true)} aria-label={t('track_trace.assets.actions.minimize_details')}><ChevronDown size={20} /></Button>
                        <Button variant="link" onClick={onClear} aria-label={t('track_trace.assets.actions.close_details')}><X size={20} /></Button>
                    </div>
                </div>
                <div className="asset-detail-grid">
                    <span>{t('track_trace.assets.fields.type')}</span><strong>{selectedAsset.asset_type?.name || t('track_trace.common.unknown')}</strong>
                    <span>{t('track_trace.assets.fields.status')}</span><strong>{selectedAsset.status_label || t(`track_trace.statuses.${selectedAsset.status}`, { defaultValue: selectedAsset.status || t('track_trace.common.unknown') })}</strong>
                    <span>{t('track_trace.assets.fields.zone')}</span><strong>{selectedAsset.current_location?.location_name || t('track_trace.assets.no_current_location')}</strong>
                    <span>{t('track_trace.assets.fields.tag')}</span><strong>{primaryTag?.identifier || t('track_trace.assets.no_tag')}</strong>
                    <span>{t('track_trace.assets.fields.battery')}</span><strong>{primaryTag?.battery_level ?? t('track_trace.common.unknown')}</strong>
                    <span>{t('track_trace.assets.fields.last_seen')}</span><strong>{formatTranslatedDateTime(selectedAsset.current_location?.last_seen_at)}</strong>
                </div>
                {freshness !== 'recent' && <Badge bg="warning" text="dark" className="mt-2">{t('track_trace.assets.location_freshness', { freshness: freshnessLabel(freshness) })}</Badge>}
            </div>
        );
    }

    return (
            <div className="asset-bottom-sheet">
                <div className="sheet-title-row">
                    <div>
                        <h2>{t('track_trace.assets.visible_assets', { count: payload.summary.visible_assets })}</h2>
                        <p>{t('track_trace.assets.summary_updated', { total: payload.summary.total_assets, date: formatTranslatedDateTime(payload.generated_at) })}</p>
                    </div>
                    <Button variant="link" onClick={() => onToggleCollapsed(true)} aria-label={t('track_trace.assets.actions.minimize_summary')}><ChevronDown size={20} /></Button>
                </div>
            <div className="summary-counts">
                {payload.summary.asset_type_counts.map((item) => (
                    <span key={String(item.asset_type_id)}>{item.asset_type_name}: <strong>{item.count}</strong></span>
                ))}
            </div>
            <div className="asset-sheet-list">
                {payload.assets.slice(0, 12).map((asset) => (
                    <button key={asset.id} onClick={() => onSelectAsset(asset)}>
                        <span>{asset.name}</span>
                        <small>{asset.current_location?.location_name || t('track_trace.assets.no_current_position')}</small>
                    </button>
                ))}
            </div>
        </div>
    );
};

export default AssetBottomSheet;
